import React, { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Add, Computer } from '@mui/icons-material';
import { useSearch } from '../../hooks/useSearch';
import { useToggle } from '../../hooks/useToggle';
import { useValidateReload } from '../../hooks/useValidateReload';
import {
  getElectronicEquipmentTypes,
  getElectronicEquipment,
  setElectronicEquipmentSearchFilters
} from '../../redux/actions/electronicEquipment/electronicEquipmentActions';
import { selectElectronicEquipmentTableData } from './electronicEquipmentSelectors';
import { loadingActions } from '../../redux/reducers/electronicEquipment/types';
import CardTable from '../../components/CardTable/CardTable';
import RegularButton from '../../components/CustomButtons/Button';
import { TableInputs } from "../../components/CardTable/TableInputs";
import ElectronicEquipmentForm from './ElectronicEquipmentForm';
import { columns } from './columns';

// Argument to use in useValidateReload
const loadings = Object.values(loadingActions);

export default function ElectronicEquipmentView() {
  const dispatch = useDispatch();
  const { setSnackbar } = useSearch();

  // Redux state
  const { equipmentTypes, isLoadingComp, station, hasSearched } = useSelector(state => ({
    equipmentTypes: state.electronicEquipment?.equipmentTypes,
    isLoadingComp: state.search?.isLoadingComp,
    station: state.electronicEquipment?.station,
    hasSearched: state.electronicEquipment?.hasSearched,
  }), shallowEqual);
  // Use the memoized selector for table data
  const tableData = useSelector(selectElectronicEquipmentTableData);
  const loading = isLoadingComp?.get;

  // Local state
  const [isOpen, toggleModal] = useToggle();

  const handleSearch = useCallback(() => {
    if (!station) {
      setSnackbar("No se ha seleccionado alguna planta.");
      return;
    }
    dispatch(getElectronicEquipment(station.id));
  }, [station]);

  const handleCreate = useCallback(() => {
    if (!station) {
      setSnackbar("Seleccione una planta y busque los equipos antes de crear uno nuevo.", "warning");
      return;
    }
    if (!hasSearched) {
      setSnackbar("Busque los equipos de la planta antes de crear uno nuevo.", "warning");
      return;
    }
    toggleModal();
  }, [station, hasSearched, toggleModal]);

  // Fetch equipment types on mount
  useValidateReload(loadings, getElectronicEquipmentTypes, equipmentTypes);

  return (
    <>
      <CardTable
        hasHeader
        title="Catálogo de Equipos electrónicos"
        columns={columns}
        data={tableData}
        isLoading={loading}
        icon={<Computer />}
        tableContent={
          <TableInputs
            reducer="electronicEquipment"
            stations
            dispatchAction={setElectronicEquipmentSearchFilters}
            loading={loading}
            submitAction={handleSearch}
            preventEvent
          />
        }
        hasControl
        blockBtn
        controls={
          <RegularButton
            round
            justIcon
            color="info"
            style={{ margin: 10 }}
            onClick={handleCreate}
          >
            <Add style={{ margin: 0 }} />
          </RegularButton>
        }
      />

      {isOpen && (
        <ElectronicEquipmentForm
          isOpen={isOpen}
          onClose={toggleModal}
          selectedStation={station}
        />
      )}
    </>
  );
}
